import { useState, useEffect } from 'react'
import { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { User as AppUser } from '../types'

// Demo user type
interface DemoUser {
  id: string;
  email: string;
  full_name: string;
  role: 'citizen' | 'official' | 'admin';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)

  useEffect(() => {
    // Check for demo user first
    const savedDemoUser = localStorage.getItem('demoUser')
    if (savedDemoUser) {
      try {
        const demoUser: DemoUser = JSON.parse(savedDemoUser)
        const appUser: AppUser = {
          id: demoUser.id,
          email: demoUser.email,
          full_name: demoUser.full_name,
          role: demoUser.role,
          is_active: demoUser.is_active,
          created_at: demoUser.created_at,
          updated_at: demoUser.updated_at
        }
        setUser(appUser)
        setLoading(false)
        return
      } catch (error) {
        console.error('Error parsing demo user:', error)
        localStorage.removeItem('demoUser')
      }
    }

    // Get initial session for Supabase auth
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setSession(session)
        
        if (session?.user) {
          // Create a fallback user immediately for UI to show
          const fallbackUser = {
            id: session.user.id,
            email: session.user.email,
            phone: session.user.phone,
            full_name: session.user.user_metadata?.full_name || 'User',
            role: 'citizen' as const,
            department: undefined,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
          setUser(fallbackUser)
          
          // Try to fetch from database but don't block UI
          fetchUserProfile(session.user.id).catch(console.error)
        }
        
        setLoading(false)
      } catch (error) {
        console.error('Error getting session:', error)
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        
        if (session?.user) {
          // Create fallback user immediately for UI
          const fallbackUser = {
            id: session.user.id,
            email: session.user.email,
            phone: session.user.phone,
            full_name: session.user.user_metadata?.full_name || 'User',
            role: 'citizen' as const,
            department: undefined,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
          setUser(fallbackUser)
          
          // Try to fetch real profile but don't block UI
          fetchUserProfile(session.user.id).catch(console.error)
        } else {
          setUser(null)
        }
        
        // Always set loading to false after processing
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching user profile:', error)
        // If user doesn't exist in users table, that's okay - fallback user is already set
        if (error.code === 'PGRST116') {
          console.log('User profile not found in database, using fallback profile')
        }
        return
      }

      // Only update if we got real data from database
      if (data) {
        setUser(data)
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error)
      // Don't throw - let the UI continue with fallback user
    }
  }

  const signInWithOTP = async (identifier: string, type: 'email' | 'sms') => {
    const options = type === 'email' 
      ? { email: identifier }
      : { phone: identifier }

    const { error } = await supabase.auth.signInWithOtp(options)
    return { error }
  }

  const verifyOTP = async (identifier: string, token: string, type: 'email' | 'sms') => {
    const options = type === 'email'
      ? { email: identifier, token, type: 'email' as const }
      : { phone: identifier, token, type: 'sms' as const }

    const { data, error } = await supabase.auth.verifyOtp(options)
    
    if (data.user && !error) {
      // Create user profile if it doesn't exist
      const { error: profileError } = await supabase
        .from('users')
        .upsert({
          id: data.user.id,
          email: data.user.email,
          phone: data.user.phone,
          role: 'citizen',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
      
      if (profileError) {
        console.error('Error creating user profile:', profileError)
      }
    }
    
    return { data, error }
  }

  const signOut = async () => {
    // Clear demo user if exists
    localStorage.removeItem('demoUser')
    
    const { error } = await supabase.auth.signOut()
    if (!error) {
      setUser(null)
      setSession(null)
    }
    return { error }
  }

  const updateProfile = async (updates: Partial<AppUser>) => {
    if (!user) return { error: new Error('No user logged in') }

    const { error } = await supabase
      .from('users')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id)

    if (!error) {
      setUser({ ...user, ...updates })
    }

    return { error }
  }

  // Development helper to override role temporarily
  const setDevRole = (role: 'citizen' | 'official' | 'admin') => {
    if (process.env.NODE_ENV === 'development' && user) {
      setUser({ ...user, role })
    }
  }

  return {
    user,
    session,
    loading,
    signInWithOTP,
    verifyOTP,
    signOut,
    logout: signOut, // Alias for signOut
    updateProfile,
    setDevRole,
    isAuthenticated: !!session || !!localStorage.getItem('demoUser'),
    isAdmin: user?.role === 'admin',
    isOfficial: user?.role === 'official' || user?.role === 'admin',
    isCitizen: user?.role === 'citizen'
  }
}